import React, { useEffect, useMemo, useState } from 'react';
import { History, RotateCcw, GitBranch, X, Loader2, Wand2, Hand, Sparkles, Layers, GitCompare, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { versionsApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { DiffViewerDialog } from './DiffViewerDialog';
import ProvenanceWidget from '../../ProvenanceWidget';

export const SOURCE_META = {
  'ai-generate':   { label: 'AI · Generate',   Icon: Wand2,    cls: 'text-primary' },
  'ai-continue':   { label: 'AI · Continue',   Icon: Wand2,    cls: 'text-primary' },
  'ai-revise':     { label: 'AI · Revise',     Icon: Sparkles, cls: 'text-primary' },
  'ai-build':      { label: 'AI · Build novel',Icon: Layers,   cls: 'text-primary' },
  'manual':        { label: 'Manual edit',     Icon: Hand,     cls: 'text-foreground/70' },
  'pre-restore':   { label: 'Before restore',  Icon: RotateCcw,cls: 'text-warning' },
  'branch-source': { label: 'Branch source',   Icon: GitBranch,cls: 'text-success' },
  // iter-14 — snapshot taken right before accepting a plagiarism rewrite.
  'pre-plagiarism-accept': { label: 'Before plagiarism rewrite', Icon: ShieldCheck, cls: 'text-warning' },
  // Snapshot taken right before an editor's direct commit from the
  // Editorial review workspace (Ask AI rewrite, hand-edited then
  // committed straight into the chapter — no author approval step).
  'pre-editor-commit': { label: 'Before editor direct edit', Icon: ShieldCheck, cls: 'text-warning' },
};

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}

export function VersionHistoryDrawer({ chapter, projectId, open, onClose, onRestored, canMutate = true }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // Diff viewer state — opens when user clicks Compare on a row.
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffCandidate, setDiffCandidate] = useState(null);  // the row clicked
  const [diffBaseline, setDiffBaseline] = useState(null);    // what we compare AGAINST

  useEffect(() => {
    if (!open || !chapter?.id) return;
    let cancelled = false;
    setLoading(true);
    versionsApi.list(chapter.id)
      .then((r) => { if (!cancelled) setVersions(r.versions || []); })
      .catch(() => { if (!cancelled) toast.error('Could not load version history.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, chapter?.id]);

  const refresh = async () => {
    if (!chapter?.id) return;
    setLoading(true);
    try { const r = await versionsApi.list(chapter.id); setVersions(r.versions || []); }
    catch { toast.error('Could not refresh version history.'); }
    finally { setLoading(false); }
  };

  const handleRestore = async (v) => {
    if (!canMutate) { toast.warning('Your role cannot restore versions.'); return; }
    if (!window.confirm(`Restore "${v.title}" (v${v.version_number})?\nYour current draft will be snapshotted first so you can undo.`)) return;
    setBusyId(v.id);
    try {
      await versionsApi.restore(chapter.id, v.id);
      toast.success(`Restored v${v.version_number}.`);
      onRestored?.();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed.');
    } finally { setBusyId(null); }
  };

  const handleBranch = async (v) => {
    if (!canMutate) { toast.warning('Your role cannot branch versions.'); return; }
    setBusyId(v.id);
    try {
      const r = await versionsApi.branch(chapter.id, v.id);
      const newCh = r.chapter;
      toast.success(`Branched into Chapter ${newCh.number}: "${newCh.title}".`);
      onRestored?.();   // parent reloads chapter list
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Branch failed.');
    } finally { setBusyId(null); }
  };

  const handleCompare = (v) => {
    // Default the baseline to the version immediately *before* the clicked one
    // (i.e. the next-older snapshot). Versions are newest-first.
    const idx = versions.findIndex((x) => x.id === v.id);
    const baseline = idx >= 0 && idx + 1 < versions.length ? versions[idx + 1] : null;
    setDiffCandidate(v);
    setDiffBaseline(baseline);
    setDiffOpen(true);
  };

  const summary = useMemo(() => {
    const ai = versions.filter(v => v.source?.startsWith('ai-')).length;
    const total = versions.length;
    return `${total} version${total === 1 ? '' : 's'} · ${ai} AI · ${total - ai} manual/restore`;
  }, [versions]);

  if (!open) return null;

  return (
    <div
      data-testid="version-history-drawer"
      className="absolute inset-y-0 right-0 z-30 flex w-full max-w-[420px] flex-col border-l border-panel-border bg-card shadow-panel"
    >
      <div className="flex items-center gap-2 border-b border-panel-border bg-panel-header px-3 py-2">
        <History className="h-4 w-4 text-primary" />
        <div className="flex-1">
          <div className="text-[13.5px] font-semibold text-foreground">Version history</div>
          <div className="text-[13px] text-muted-foreground">{summary}</div>
        </div>
        <button
          onClick={onClose}
          data-testid="version-history-close"
          className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-auto scroll-thin p-2 space-y-1.5">
        {projectId && (
          <div className="mb-1" data-testid="version-history-provenance-slot">
            <ProvenanceWidget projectId={projectId} />
          </div>
        )}
        {loading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="ml-2 text-[13.5px]">Loading versions…</span>
          </div>
        )}
        {!loading && versions.length === 0 && (
          <div className="rounded-md border border-dashed border-panel-border bg-background/40 p-6 text-center">
            <History className="mx-auto h-5 w-5 text-muted-foreground/70" />
            <p className="mt-2 text-[13.5px] text-foreground/80">No versions yet.</p>
            <p className="text-[13px] text-muted-foreground">
              The next AI generation or manual edit will create the first snapshot.
            </p>
          </div>
        )}
        {!loading && versions.map((v) => {
          const meta = SOURCE_META[v.source] || { label: v.source, Icon: Layers, cls: 'text-muted-foreground' };
          const Icon = meta.Icon;
          const isBusy = busyId === v.id;
          return (
            <div
              key={v.id}
              data-testid={`version-row-${v.version_number}`}
              className="rounded-md border border-border bg-background/60 p-2.5"
            >
              <div className="flex items-start gap-2">
                <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-secondary', meta.cls)}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-mono-stat text-[13px] text-muted-foreground">v{v.version_number}</span>
                    <span className="truncate text-[13.5px] font-medium text-foreground">{v.title || `Chapter ${chapter?.number}`}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[13px] text-muted-foreground">
                    <span className={meta.cls}>{meta.label}</span>
                    <span>·</span>
                    <span className="font-mono-stat">{(v.words || 0).toLocaleString()} words</span>
                    <span>·</span>
                    <span>{relTime(v.created_at)}</span>
                  </div>
                  {v.note && <div className="mt-1 truncate text-[13px] text-muted-foreground/80 italic">“{v.note}”</div>}
                  <div className="mt-2 flex items-center gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!canMutate || isBusy}
                      onClick={() => handleRestore(v)}
                      data-testid={`version-restore-v${v.version_number}`}
                      className="h-6 gap-1 px-2 text-[13px] disabled:opacity-50"
                    >
                      {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                      Restore
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isBusy}
                      onClick={() => handleCompare(v)}
                      data-testid={`version-compare-v${v.version_number}`}
                      className="h-6 gap-1 px-2 text-[13px] text-primary hover:text-primary"
                    >
                      <GitCompare className="h-3 w-3" /> Compare
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!canMutate || isBusy}
                      onClick={() => handleBranch(v)}
                      data-testid={`version-branch-v${v.version_number}`}
                      className="h-6 gap-1 px-2 text-[13px] text-success hover:text-success disabled:opacity-50"
                    >
                      <GitBranch className="h-3 w-3" /> Branch
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!canMutate && (
        <div className="border-t border-panel-border bg-warning/10 px-3 py-1.5 text-[13px] text-warning-foreground">
          Read-only members cannot restore or branch versions.
        </div>
      )}

      <DiffViewerDialog
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        chapter={chapter}
        baseline={diffBaseline}
        candidate={diffCandidate}
        versions={versions}
        onChangeBaseline={setDiffBaseline}
        onRestore={() => { onRestored?.(); refresh(); }}
        canMutate={canMutate}
      />
    </div>
  );
}
